import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ReferenceStatusForm from './ReferenceStatusForm';
import FlowStatusForm from './FlowStatusForm';
import DelineationStatusForm from './DelineationStatusForm';
import EmailVerification from './EmailVerification';
import { apiServices } from '../services';

// Storage key for verified emails in localStorage
const VERIFIED_EMAILS_KEY = 'refscreens_verified_emails';

// Check if any email is verified locally
const getVerifiedEmail = () => {
  try {
    const stored = localStorage.getItem(VERIFIED_EMAILS_KEY);
    const emails = stored ? JSON.parse(stored) : [];
    return emails.length > 0 ? emails[emails.length - 1] : null;
  } catch {
    return null;
  }
};

const FeedbackModal = ({ 
  open, 
  onClose, 
  masterid, 
  refstatusFinal,
  flowstatusFinal
}) => {
  const [selectedFeedbackType, setSelectedFeedbackType] = useState(null); // 'reference' or 'flow'
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(null);

  // Check for verified email when modal opens
  useEffect(() => {
    if (open) {
      const email = getVerifiedEmail();
      setVerifiedEmail(email);
    }
  }, [open]);

  const handleEmailVerified = (email) => {
    setVerifiedEmail(email);
  };

  const handleClose = () => {
    setSelectedFeedbackType(null);
    setSubmitError(null);
    onClose();
  };

  const handleFeedbackTypeSelect = (type) => {
    setSelectedFeedbackType(type);
  };

  const handleBackToSelection = () => {
    setSelectedFeedbackType(null);
    setSubmitError(null);
  };

  const handleSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Add verified email to the data
      const dataWithEmail = { ...data, email: verifiedEmail };
      
      let response;
      if (selectedFeedbackType === 'reference') {
        response = await apiServices.submitRefstatusFeedback(dataWithEmail);
      } else if (selectedFeedbackType === 'flow') {
        response = await apiServices.submitFlowstatusFeedback(dataWithEmail);
      } else if (selectedFeedbackType === 'delineation') {
        response = await apiServices.submitDelineationFeedback(dataWithEmail);
      }
      
      if (response.success) {
        setSubmitSuccess(true);
        // Close the dialog immediately after success
        handleClose();
        // Hide the success message after 3 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 3000);
      } else {
        setSubmitError(response.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitError(error.message || 'An error occurred while submitting feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    // If no verified email, show email verification first
    if (!verifiedEmail) {
      return (
        <EmailVerification
          onVerified={handleEmailVerified}
          onCancel={handleClose}
        />
      );
    }

    // If no feedback type selected, show the selection screen
    if (!selectedFeedbackType) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
          <Alert severity="success" sx={{ mb: 1 }}>
            Verified as: <strong>{verifiedEmail}</strong>
          </Alert>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Please select the type of feedback you would like to provide:
          </Typography>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => handleFeedbackTypeSelect('reference')}
            sx={{ 
              py: 2, 
              justifyContent: 'flex-start',
              textAlign: 'left',
              textTransform: 'none'
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Reference Status Feedback
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Would you like to provide feedback on the reference status for this site?
              </Typography>
            </Box>
          </Button>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => handleFeedbackTypeSelect('flow')}
            sx={{ 
              py: 2, 
              justifyContent: 'flex-start',
              textAlign: 'left',
              textTransform: 'none'
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Flow Status Feedback
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Would you like to provide feedback on the flow status for this site?
              </Typography>
            </Box>
          </Button>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => handleFeedbackTypeSelect('delineation')}
            sx={{ 
              py: 2, 
              justifyContent: 'flex-start',
              textAlign: 'left',
              textTransform: 'none'
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Delineation Feedback
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Do you want to report a problem with the delineation?
              </Typography>
            </Box>
          </Button>
        </Box>
      );
    }

    // Show the appropriate form based on selection
    if (selectedFeedbackType === 'reference') {
      return (
        <>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <ReferenceStatusForm
            masterid={masterid}
            refstatusFinal={refstatusFinal}
            onSubmit={handleSubmit}
            onCancel={handleBackToSelection}
            isSubmitting={isSubmitting}
            verifiedEmail={verifiedEmail}
          />
        </>
      );
    }

    if (selectedFeedbackType === 'flow') {
      return (
        <>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <FlowStatusForm
            masterid={masterid}
            flowstatusFinal={flowstatusFinal}
            onSubmit={handleSubmit}
            onCancel={handleBackToSelection}
            isSubmitting={isSubmitting}
            verifiedEmail={verifiedEmail}
          />
        </>
      );
    }

    if (selectedFeedbackType === 'delineation') {
      return (
        <>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <DelineationStatusForm
            masterid={masterid}
            onSubmit={handleSubmit}
            onCancel={handleBackToSelection}
            isSubmitting={isSubmitting}
            verifiedEmail={verifiedEmail}
          />
        </>
      );
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { minHeight: 300 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          bgcolor: 'info.main',
          color: 'info.contrastText'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedFeedbackType && verifiedEmail && (
              <Button 
                size="small" 
                onClick={handleBackToSelection}
                sx={{ color: 'inherit', minWidth: 'auto', mr: 1 }}
              >
                ← Back
              </Button>
            )}
            <Typography variant="h6">
              {!verifiedEmail 
                ? 'Verify Your Email'
                : selectedFeedbackType 
                  ? (selectedFeedbackType === 'reference' 
                      ? 'Reference Status Feedback' 
                      : selectedFeedbackType === 'flow' 
                        ? 'Flow Status Feedback' 
                        : 'Delineation Feedback')
                  : 'Provide Feedback'
              }
            </Typography>
          </Box>
          <IconButton 
            onClick={handleClose}
            size="small"
            sx={{ color: 'inherit' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {renderContent()}
        </DialogContent>

        {!selectedFeedbackType && verifiedEmail && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Success notification */}
      <Snackbar
        open={submitSuccess}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">
          Thank you! Your feedback has been submitted successfully.
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackModal;
